import React from 'react';
import { FormGroup, Label, Input } from 'reactstrap';
import RichEditor from 'Component/Editor/RichEditor/RichEditor';
import ModalComponent from 'Component/Learning/Common/Modal/ModalGeneral';
import RawComponent from 'Component/Learning/Common/RawHTML';
import AdminShapePreview from 'Component/Learning/BankQuestion/AdminShapePreview';
import AdminDragPinPreview from 'Component/Learning/BankQuestion/AdminDragPinPreview';
import AdminSequentialPreview from 'Component/Learning/BankQuestion/AdminSequentialPreview';
import AdminBarPreview from 'Component/Learning/BankQuestion/AdminBarPreview';
import AdminLinePreview from 'Component/Learning/BankQuestion/AdminLinePreview';
import AdminPiePreview from 'Component/Learning/BankQuestion/AdminPiePreview';

/**
 * QuestionMainFieldsSection (presentational)
 * - Render "Label Soal" + "Pertanyaan" (RichEditor) + tombol preview modal (jika ada description)
 * - Parent tetap pegang state/dispatch via props handlers
 */
export default function QuestionMainFieldsSection(props) {
  const {
    title,
    onChangeTitle,
    description,
    answerExplanation,
    onChangeDescription,
    choice = [],
    answer = [],
    questionType,
    interactiveType,
    clickableType,
    clickableVariant,
    clickablePreset,
    dragpinRoomCount,
    dragpinPinCount,
    dragpinPinCustomNames,
    dragpinRoomCustomNames,
    dragpinPins,
    dragpinAnswer,
    dragpinKeyAnswer,
    barChartStep,
    barChartMinMax,
    lineChartStep,
    lineChartMinMax,
    pieChartMinProportion,
    isTitleLocked = false,
  } = props;

  // Clickable preview: tampilkan Konva (AdminShapePreview) sebagai pengganti pilihan jawaban
  const isClickablePreview =
    questionType === 'fill_in_the_blanks' &&
    interactiveType === 'clickable' &&
    Boolean(clickableType);

  // Dragpin preview: tampilkan AdminDragPinPreview sebagai pengganti pilihan jawaban
  const isDragpinPreview =
    questionType === 'fill_in_the_blanks' &&
    interactiveType === 'dragpin' &&
    dragpinRoomCount && dragpinPinCount;

  // Sequential preview: tampilkan AdminSequentialPreview sebagai pengganti pilihan jawaban
  const isSequentialPreview =
    questionType === 'sentence_arrangement' &&
    interactiveType === 'sequential' &&
    choice.length > 0;

  // Bar chart preview: tampilkan AdminBarPreview sebagai pengganti pilihan jawaban
  const isBarChartPreview =
    questionType === 'sentence_arrangement' &&
    interactiveType === 'bar_chart' &&
    choice.length > 0;

  // Line chart preview: tampilkan AdminLinePreview sebagai pengganti pilihan jawaban
  const isLineChartPreview =
    questionType === 'sentence_arrangement' &&
    interactiveType === 'line_chart' &&
    choice.length > 0;

  // Pie chart preview: tampilkan AdminPiePreview sebagai pengganti pilihan jawaban
  const isPieChartPreview =
    questionType === 'sentence_arrangement' &&
    interactiveType === 'pie_chart' &&
    choice.length > 0;

  return (
    <>
      <FormGroup className="mb-3">
        <Label for="validationTitle" style={{ fontWeight: 'bold' }}>
          {'Label Soal'}:
        </Label>
        <Input
          className="form-control"
          id="validationTitle"
          type="text"
          name="title"
          placeholder="Tuliskan label soal..."
          value={title || ''}
          onChange={onChangeTitle}
          disabled={Boolean(isTitleLocked)}
          style={isTitleLocked ? { opacity: 0.72 } : undefined}
        />
      </FormGroup>

      <FormGroup className="mb-3">
        <div style={{ display: 'flex', alignItems: 'top' }}>
          <Label
            for="validationImage"
            style={{ fontWeight: 'bold', marginBottom: '15px', marginRight: '10px' }}
          >
            Pertanyaan :
          </Label>

          {/* Preview hanya tampil jika ada konten pertanyaan */}
          {description && (
            <ModalComponent
              title="Preview pertanyaan"
              type="component"
              buttonLabel={<i className="fa fa-info-circle fa-lg"></i>}
              buttonSize="xs"
              size="lg"
              messageTooltip="Preview pertanyaan"
            >
              <div>
                {/* Pertanyaan */}
                <div style={{ marginBottom: '20px' }}>
                  <RawComponent htmlContent={description} />
                </div>

                {/* Pilihan Jawaban */}
                {isClickablePreview && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <AdminShapePreview
                      variant={clickableVariant}
                      preset={clickablePreset}
                      readOnly={true}
                      height={420}
                    />
                  </div>
                )}

                {isDragpinPreview && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <AdminDragPinPreview
                      isExplanation={true}
                      persons={dragpinPins || []}
                      presetRoomCount={dragpinRoomCount}
                      presetPinCount={dragpinPinCount}
                      presetPinCustomNames={dragpinPinCustomNames}
                      presetRoomCustomNames={dragpinRoomCustomNames}
                      presetExplanation={{
                        userAnswerText: Array.isArray(dragpinAnswer) ? dragpinAnswer : [],
                        keyAnswerText: Array.isArray(dragpinKeyAnswer) ? dragpinKeyAnswer : []
                      }}
                    />
                  </div>
                )}

                {isSequentialPreview && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <AdminSequentialPreview
                      items={choice}
                      isExplanation={true}
                      presetOrder={answer}
                    />
                  </div>
                )}

                {isBarChartPreview && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <AdminBarPreview
                      items={choice}
                      isExplanation={true}
                      presetValues={answer}
                      minMax={barChartMinMax}
                      step={barChartStep}
                    />
                  </div>
                )}

                {isLineChartPreview && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <AdminLinePreview
                      items={choice}
                      isExplanation={true}
                      presetValues={answer}
                      minMax={lineChartMinMax}
                      step={lineChartStep}
                    />
                  </div>
                )}

                {isPieChartPreview && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <AdminPiePreview
                      items={choice}
                      isExplanation={true}
                      presetPercentages={answer}
                      minProportion={pieChartMinProportion}
                    />
                  </div>
                )}

                {!isClickablePreview && questionType === 'multiple_choice' && choice.length > 0 && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <div style={{ paddingLeft: '10px' }}>
                      {choice.map((item, idx) => (
                        <div key={idx} style={{ marginBottom: '10px' }}>
                          <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                            {/* <span style={{ fontWeight: 'bold', marginRight: '8px', minWidth: '25px' }}>
                              {String.fromCharCode(65 + idx)}.
                            </span> */}
                            <div style={{ flex: 1 }}>
                              <RawComponent htmlContent={item} />
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {!isClickablePreview && !isDragpinPreview && !isSequentialPreview && !isBarChartPreview && !isLineChartPreview && !isPieChartPreview && (questionType === 'fill_in_the_blanks' || questionType === 'sentence_arrangement') && answer.length > 0 && (
                  <div>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pilihan Jawaban:</h6>
                    <div style={{ paddingLeft: '10px' }}>
                      {answer.map((item, idx) => (
                        <div key={idx} style={{ marginBottom: '8px' }}>
                          <span style={{ fontWeight: 'bold', marginRight: '8px' }}>
                            {idx + 1}.
                          </span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Pembahasan selalu diletakkan paling bawah pada preview modal. */}
                {!!answerExplanation && (
                  <div style={{ marginTop: '20px' }}>
                    <h6 style={{ fontWeight: 'bold', marginBottom: '15px' }}>Pembahasan:</h6>
                    <div style={{ paddingLeft: '10px' }}>
                      <RawComponent htmlContent={answerExplanation} />
                    </div>
                  </div>
                )}
              </div>
            </ModalComponent>
          )}
        </div>

        <RichEditor
          id="validationDescription"
          handleChangeDescription={onChangeDescription}
          description={description}
        />
      </FormGroup>
    </>
  );
}